import React, { useEffect } from "react"
import { BiChevronDown } from "react-icons/bi"

import {
  Button,
  Checkbox,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  SimpleGrid,
  Stack,
  Tooltip,
  useBreakpointValue,
  useCheckboxGroup,
} from "@chakra-ui/react"

export type FilterOption = {
  label: string
  value: string | boolean
}

export type FilterConfig = {
  filterId: string
  filterName: string
  filterOptions: FilterOption[]
  selectedValues: string[]
  hidden?: boolean
  isDisabled?: boolean
  disabledReason?: string
}

type FilterValue = Array<string | number> | null

type FilterProps = FilterConfig & {
  onFilterSet: (filterName: string, value: FilterValue) => void
  singleColumnLayout?: boolean
}

export const Filter = ({
  filterName,
  filterId,
  filterOptions,
  selectedValues,
  onFilterSet,
  singleColumnLayout,
  isDisabled,
  disabledReason,
}: FilterProps) => {
  const { value, setValue, getCheckboxProps } = useCheckboxGroup({
    defaultValue: selectedValues || [],
  })
  const buttonSize = useBreakpointValue({ base: "sm", xs: "sm", lg: "lg" })
  const singleColumn =
    typeof singleColumnLayout === "boolean"
      ? singleColumnLayout
      : filterOptions.length < 5

  useEffect(() => {
    // Allow filter to have state independent of selectedValues until
    // selectedValues have been set. This allows to check checkboxes without
    // immediately applying the filter state.
    setValue(selectedValues || [])
  }, [selectedValues])

  if (isDisabled) {
    return (
      <Tooltip label={disabledReason}>
        <Button
          size="md"
          variant="dropdown"
          colorScheme="tertiary"
          fontWeight={600}
          disabled
        >
          {filterName}
          <BiChevronDown size={25} />
        </Button>
      </Tooltip>
    )
  }

  return (
    <Popover placement="bottom-start">
      {({ onClose }) => {
        const clearFilter = () => {
          onFilterSet(filterId, null)
          setValue([])
          onClose()
        }
        const activateFilterOptions = () => {
          onFilterSet(filterId, value)
          onClose()
        }

        return (
          <>
            <PopoverTrigger>
              <Button
                size="md"
                variant="dropdown"
                colorScheme="tertiary"
                fontWeight={600}
              >
                {filterName}
                <BiChevronDown size={25} />
              </Button>
            </PopoverTrigger>

            <PopoverContent
              width={singleColumn ? "18rem" : { base: "18rem", md: "36rem" }}
              maxWidth="100vw"
              p={{ base: 3, md: 6 }}
            >
              <PopoverBody>
                <SimpleGrid
                  columns={singleColumn ? 1 : { base: 1, md: 2 }}
                  gap={2}
                >
                  {filterOptions.map((option) => (
                    <Checkbox
                      key={String(option.value)}
                      {...getCheckboxProps({ value: option.value })}
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </PopoverBody>

              <PopoverFooter mt={6} border="none">
                <Stack
                  direction="row"
                  align="center"
                  justify="center"
                  spacing={8}
                >
                  <Button
                    onClick={clearFilter}
                    size={buttonSize}
                    variant="link"
                    colorScheme="secondary"
                  >
                    Clear
                  </Button>

                  <Button size={buttonSize} onClick={activateFilterOptions}>
                    Apply filter
                  </Button>
                </Stack>
              </PopoverFooter>
            </PopoverContent>
          </>
        )
      }}
    </Popover>
  )
}
